import React, { FunctionComponent } from "react";
import styles from "./AnalyticsFilter.module.css";
import Select from "react-select";
import { connect } from "react-redux";
import { themeColors } from "../theme";

/**
 * Props for OriginationFilter component
 *
 * - organizationIdNameData: List of {label, value} pairs for dropdown
 * - setOrganizationSelectId: Updates selected organization in parent component
 * - organizationSelectId: Currently selected organization value
 * - setshowOrgFilter: Shows/Hides the dropdown container
 * - theme: Indicates light or dark mode (from Redux store)
 */
type Props = {
  organizationIdNameData: any;
  setOrganizationSelectId: (data: any) => void;
  organizationSelectId: any;
  setshowOrgFilter: (data: boolean) => void;
  theme: any;
};

/**
 * OriginationFilter Component
 *
 * A dropdown used to select an organization (or source) inside Analytics.
 * It uses react-select and applies theme-based styling dynamically.
 */
const OriginationFilter: FunctionComponent<Props> = ({
  organizationIdNameData,
  setOrganizationSelectId,
  organizationSelectId,
  setshowOrgFilter,
  theme,
}) => {
  /**
   * Container background color based on theme
   * - Matches the design system `themeColors`
   */
  const containerStyles = {
    backgroundColor: theme
      ? themeColors.dropdownBackgroundColor
      : themeColors.dropdownBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  };

  /**
   * Custom styling for react-select components
   * Applied to control, menu, selected value, and options
   */
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: theme
        ? themeColors.backgroundColor
        : themeColors.backgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    }),

    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme
        ? themeColors.dropdownBackgroundColor
        : themeColors.dropdownBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    }),

    // Selected value styling
    singleValue: (provided: any) => ({
      ...provided,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    }),

    // Option styling (hover highlight)
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? "#363853" // Hover background
        : provided.backgroundColor,
      color: state.isFocused ? "white" : provided.color,
    }),
  };

  return (
    <div className={styles.parent} style={containerStyles}>
      {/* Dropdown for selecting an organization */}
      <Select
        className={styles.select}
        options={organizationIdNameData}
        value={organizationSelectId}
        onChange={(data) => setOrganizationSelectId(data)}
        styles={customStyles}
      />
    </div>
  );
};

/**
 * Redux state mapping
 * Retrieves current theme mode (light/dark)
 */
const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode,
  };
};

export default connect(mapStateToProps)(OriginationFilter);
